import { Box, CircularProgress, DialogContent, Divider, SvgIcon, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AngleDownIcon from '@hapana/shared/web/styles/assets/svgs/angle-down-small.svg'
import ExclamationCircleSmall from '@hapana/shared/web/styles/assets/svgs/exclamation-circle-small.svg'
import User from '@hapana/shared/web/styles/assets/svgs/profile.svg'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import useSiteStore from '_stores/site.store'
import { useSiteConfiguration } from '@hapana/shared/web/hooks/siteConfiguration.hooks'
import { formatDistanceToNowAbbreviated, isBirthday } from '@hapana/shared/utils/date.util'
import { PATTERNS } from '@hapana/shared/utils/validations.util'
import EmptySearch from '_components/check-in-widget/manual-check-in/empty-search.component'
import ManualCheckIn from '_components/check-in-widget/manual-check-in/manual-check-in.component'
import MostRecentCard from '_components/check-in-widget/most-recent-card/most-recent-card.component'
import PreviousCheckIn from '_components/check-in-widget/previous-check-in/previous-check-in.component'
import Search from '_components/check-in-widget/search/search.component'
import { CHECK_IN_CONSTANTS } from '_constants/constants'
import { playCheckInSoundForSeverity } from '_utils/check-in-sounds.util'
import { SEVERITY } from '@hapana/shared/web/components/shared/banner-alert/banner-alert.constants'
import { useCheckInSoundEnabled, useSiteEnableTanningFeatures } from '_hooks/sites.hooks'

import { useCheckIn, useCheckIns, useCheckInFCMEvent } from './check-in-tab.hooks'
import { styles } from './check-in-tab.styles'
import { PAGE_SIZE } from './check-in-tab.constants'

const CheckInTab = () => {
  const { openToast } = useToast()
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { data: configuration } = useSiteConfiguration()
  const { t } = useTranslation(['checkIn', 'common'])
  const { data: isCheckInSoundEnabled } = useCheckInSoundEnabled()
  const { data: siteEnableTanningFeatures } = useSiteEnableTanningFeatures()
  const isTanning = siteEnableTanningFeatures?.adminConfigurations?.enableTanningFeatures ?? false
  const [checkInId, setCheckInId] = useState(null)
  const [checkInIds, setCheckInIds] = useState([])
  const [newCheckIns, setNewCheckIns] = useState([])
  const [searchStringValue, setSearchString] = useState('')
  const [isInvalidSearch, setIsInvalidSearch] = useState(false)
  const [showManualCheckin, setShowManualCheckin] = useState(false)
  const { data: checkIn, isLoading: isLoadingCheckIn } = useCheckIn(checkInId)
  const {
    isLoading,
    isError,
    data,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isRefetching,
    refetch,
  } = useCheckIns({
    paginate: {
      pageSize: PAGE_SIZE,
    },
  })

  const handleManualCheckIn = useCallback(
    (partialCheckIn) => {
      setShowManualCheckin(false)
      setSearchString('')
      setNewCheckIns((prevCheckIns) => [{ checkin: partialCheckIn }, ...prevCheckIns])
    },
    [setNewCheckIns]
  )

  const isRefetchingInBackground = (isRefetching && !isFetchingNextPage) || isLoadingCheckIn
  const isRefetchingOrFetchingNextPage = isFetchingNextPage || isRefetching

  const mostRecentCheckIn = newCheckIns?.[0]?.checkin || data?.pages?.[0]?.checkIns?.items?.[0]

  const parseDateTime = (dt) => (dt ? new Date(Number(dt)) : null)

  useEffect(() => {
    if (searchStringValue.length >= CHECK_IN_CONSTANTS.minCharNeeded) {
      setShowManualCheckin(true)
    } else {
      setShowManualCheckin(false)
    }
  }, [searchStringValue])

  const handleSearchCallback = useCallback((searchValue) => {
    const isValid = searchValue?.match(PATTERNS.VALID_SEARCH_CHARS)
    setSearchString(searchValue)
    if (!isValid) {
      setIsInvalidSearch(true)
    } else {
      setIsInvalidSearch(false)
    }
  }, [])

  const handleFCMCheckIn = useCallback(
    (data) => {
      if (!data?.checkInId) {
        return openToast({
          title: t('errors:api.generic'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
          horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
        })
      }

      // Check if this check-in already exists (e.g. manual check-in added optimistically)
      const existingIndex = newCheckIns.findIndex(
        (newCheckIn) => newCheckIn.checkin.checkInId === data.checkInId
      )

      if (existingIndex > -1) {
        // Update existing check-in with final status from FCM
        const updatedCheckIns = [...newCheckIns]
        updatedCheckIns[existingIndex].checkin = {
          ...updatedCheckIns[existingIndex].checkin,
          status: data.status,
        }

        const hasPaymentsIssue = data.hasOutstandingOrFailedPayments
        const severity =
          data.status?.toUpperCase() === SEVERITY.SUCCESS.toUpperCase()
            ? hasPaymentsIssue
              ? SEVERITY.WARNING
              : SEVERITY.SUCCESS
            : SEVERITY.ERROR

        if (isCheckInSoundEnabled) {
          playCheckInSoundForSeverity(severity)
        }

        return setNewCheckIns(updatedCheckIns)
      }

      // New check-in - queue for fetching full data
      setCheckInIds((prevCheckInIds) => [...prevCheckInIds, { checkin: data }])
    },
    [newCheckIns, isCheckInSoundEnabled, openToast, t, setNewCheckIns, setCheckInIds]
  )

  useCheckInFCMEvent(siteId, handleFCMCheckIn)

  useEffect(() => {
    if (checkInIds.length > 0 && !isLoadingCheckIn) {
      setCheckInId(checkInIds[0].checkin.checkInId)
      setCheckInIds((prevCheckInIds) => prevCheckInIds.slice(1))
    }
  }, [checkInIds, isLoadingCheckIn])

  useEffect(() => {
    if (checkInId && checkIn) {
      setNewCheckIns((prevNewCheckIns) => {
        const checkInIndex = prevNewCheckIns.findIndex((c) => c.checkInId === checkInId)
        if (checkInIndex === -1) {
          return [checkIn, ...prevNewCheckIns]
        }
      })
    }
  }, [checkInId, setNewCheckIns, checkIn])

  const loadMore = () => {
    if (isError) {
      return refetch()
    }
    return fetchNextPage()
  }

  if (isLoading) {
    return (
      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.loading}>
          <CircularProgress />
        </Box>
      </DialogContent>
    )
  }

  if (!mostRecentCheckIn) {
    return (
      <>
        <Search
          searchCallback={handleSearchCallback}
          searchString={searchStringValue}
          uid="checkin"
        />
        {!showManualCheckin && (
          <Box sx={styles.searchContainer}>
            <SvgIcon sx={styles.icon} component={ExclamationCircleSmall} />
            <Typography typography="small" color="gray.56" sx={styles.searchHelperText}>
              {t('checkIn:searchHelperText')}
            </Typography>
          </Box>
        )}
        {showManualCheckin ? (
          <>
            <Divider sx={styles.thickDivider} />
            {isInvalidSearch ? (
              <EmptySearch />
            ) : (
              <ManualCheckIn
                searchValue={searchStringValue}
                onManualCheckin={handleManualCheckIn}
                isTanning={isTanning}
              />
            )}
          </>
        ) : (
          <DialogContent sx={styles.dialogContent}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              height="100%"
            >
              <SvgIcon component={User} sx={styles.userIcon} />
              <Typography typography="medium" color="gray.56" sx={styles.mostRecentTitle}>
                {t('checkIn:noCheckIns')}
              </Typography>
              {isRefetchingInBackground && <CircularProgress size={24} />}
            </Box>
          </DialogContent>
        )}
      </>
    )
  }

  const { pages } = data

  const {
    checkInId: id,
    client,
    service: membership,
    nextSession,
    failedInvoices,
    stagedInvoices,
    status,
    dateTime: datetime,
    clientId,
    failReason,
  } = mostRecentCheckIn

  const homeSiteName = client?.homeSiteName
  const mapInvoice = (inv) => ({
    invoiceId: inv.invoiceId,
    label: inv.description,
    price: inv.amount,
    datetime: inv.dueDate,
  })
  const payments = {
    failed: failedInvoices?.map(mapInvoice),
    outstanding: stagedInvoices?.map(mapInvoice),
  }

  let previousResults = pages.flatMap((page) =>
    (page.checkIns?.items || []).filter((checkIn) => checkIn.checkInId !== id)
  )

  const isTherePreviousOrNewCheckIns = previousResults?.length > 0 || newCheckIns.length > 1

  const timezone = configuration?.timezone

  return (
    <>
      <Search
        searchCallback={handleSearchCallback}
        searchString={searchStringValue}
        uid="checkin"
      />
      {!showManualCheckin && (
        <Box sx={styles.searchContainer}>
          <SvgIcon sx={styles.icon} component={ExclamationCircleSmall} />
          <Typography typography="small" color="gray.56" sx={styles.searchHelperText}>
            {t('checkIn:searchHelperText')}
          </Typography>
        </Box>
      )}
      {showManualCheckin ? (
        <>
          <Divider sx={styles.thickDivider} />
          {isInvalidSearch ? (
            <EmptySearch />
          ) : (
            <ManualCheckIn
              searchValue={searchStringValue}
              onManualCheckin={handleManualCheckIn}
              isTanning={isTanning}
            />
          )}
        </>
      ) : (
        <DialogContent sx={styles.dialogContent}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={styles.mostRecent}
          >
            <Typography typography="header4" color="gray.80" sx={styles.mostRecentTitle}>
              {t('checkIn:mostRecent')}
            </Typography>
            {isRefetchingInBackground && <CircularProgress size={24} />}
          </Box>

          <MostRecentCard
            name={`${client?.firstName} ${client?.lastName}`}
            firstName={`${client?.firstName}`}
            date={datetime ? formatDistanceToNowAbbreviated(parseDateTime(datetime)) : ''}
            location={homeSiteName}
            checkInTime={parseDateTime(datetime)}
            membership={membership ? { title: membership.serviceName } : null}
            nextSession={
              nextSession
                ? { title: nextSession.sessionName, datetime: nextSession.sessionDateTime }
                : null
            }
            status={status}
            profileImg={client?.profileImage}
            payments={payments}
            clientId={clientId}
            failReason={failReason}
            birthday={
              isBirthday(client?.dateOfBirth, parseDateTime(datetime), timezone) &&
              client?.dateOfBirth
            }
            siteNotes={client?.notes}
            totalCheckInCount={client?.totalCheckInCount}
            siteInformation={client?.siteInformation}
            homeSites={client?.homeLocationIds}
            checkInId={id}
          />
          {isTherePreviousOrNewCheckIns && (
            <>
              <Box display="flex" marginTop={4} alignItems="center" sx={styles.previousCheckIns}>
                <Typography typography="header4" color="gray.80" sx={styles.previousCheckInsTitle}>
                  {t('checkIn:previousCheckIns')}
                </Typography>
              </Box>
              <PreviousCheckIn checkIns={previousResults} newCheckIns={newCheckIns} />

              {hasNextPage && (
                <Box sx={styles.loadMoreContainer}>
                  <Button
                    variant={BUTTON_VARIANTS.OUTLINED}
                    {...(!isRefetchingOrFetchingNextPage && {
                      startIcon: AngleDownIcon,
                    })}
                    size={BUTTON_SIZE.MEDIUM}
                    theme={BUTTON_THEME.GRAY}
                    label={
                      isRefetchingOrFetchingNextPage ? (
                        <CircularProgress size={20} />
                      ) : (
                        t('common:loadMore')
                      )
                    }
                    disabled={isRefetchingOrFetchingNextPage}
                    onClick={loadMore}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
      )}
    </>
  )
}

export default CheckInTab
